import React from 'react';

import './css/ContactPage.css'

import { Page } from './Page';
import { WhoWeAre } from './WhoWeAre';
import { useCursorRimLight } from './rimLight';

import { CONTACT_EMAIL, TALK_TO_US_URL, BOOK_DEMO_LABEL } from './contact'

///@brief   How long the copy button reads "Copied" before it offers to copy
///         again.
const COPIED_FOR_MS: number = 1800


function CopyIcon(): React.JSX.Element {
	return (
		<svg className="Contact-Button-Icon" viewBox="0 0 16 16" fill="none" aria-hidden="true" >
			<rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
			<path d="M10.5 5.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5" />
		</svg>
	)
}

function CheckIcon(): React.JSX.Element {
	return (
		<svg className="Contact-Button-Icon" viewBox="0 0 16 16" fill="none" aria-hidden="true" >
			<path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
		</svg>
	)
}

///@brief   Puts `text` on the clipboard: through the async API, or, where a
///         browser refuses it, by selecting the text in an offscreen field
///         and copying the old way. False when neither took.
async function copyToClipboard( text: string ): Promise<boolean> {
	try {
		await navigator.clipboard.writeText( text )
		return true
	} catch {
		// Refused or missing: the old way below.
	}

	const field: HTMLTextAreaElement = document.createElement( 'textarea' )
	field.value = text
	field.setAttribute( 'readonly', '' )
	field.setAttribute( 'aria-hidden', 'true' )
	field.style.position = 'fixed'
	field.style.opacity = '0'
	document.body.appendChild( field )
	field.select()

	let copied: boolean = false
	try {
		copied = document.execCommand( 'copy' )
	} catch {
		copied = false
	}
	document.body.removeChild( field )
	return copied
}

// Puts the address on the clipboard and says so for a moment. When nothing
// took, it says nothing: the address is a link right above it, there to
// select by hand.
function CopyButton( { text }: { text: string } ): React.JSX.Element {

	const [ copied, setCopied ] = React.useState<boolean>( false )

	React.useEffect( (): ( () => void ) => {
		if ( !copied ) return (): void => {}
		const timeout: number = window.setTimeout( (): void => setCopied( false ), COPIED_FOR_MS )
		return (): void => window.clearTimeout( timeout )
	}, [ copied ] )

	const onClick = async (): Promise<void> => {
		if ( await copyToClipboard( text ) ) setCopied( true )
	}

	return (
		<button
			type="button"
			className={ `Contact-Button Contact-Button-Secondary${ copied ? ' is-copied' : '' }` }
			onClick={ onClick }
			aria-live="polite"
		>
			{ copied ? <CheckIcon /> : <CopyIcon /> }
			{ copied ? 'Copied' : 'Copy address' }
		</button>
	)
}


// Landing page of the menu's Contact link: the two ways in, a call and the
// shared inbox, side by side under the heading, then the founders and their
// own addresses.
export function ContactPage(): React.JSX.Element {

	const sectionRef = React.useRef<HTMLDivElement>( null )
	useCursorRimLight( sectionRef, '.Tile' )

	return (
		<Page title="Underlayer — Contact" className="Contact" >
			<div className="Pane-Light Page-Section Page-Section-First Contact-Section" ref={ sectionRef } >
				<div className="Container" >

					<div className="Contact-Head" >
						<div className="Page-Label" >Contact</div>
						<div className="Title text-shadow-2" data-fade >Let's talk.</div>
					</div>

					<div className="Contact-Ways" >
						<a
							className="Tile Tile-Link Contact-Way"
							href={ TALK_TO_US_URL }
							target="_blank"
							rel="noopener noreferrer"
							data-fade
							data-fade-delay="0.2"
						>
							<div className="Tile-Kicker" >Book a call</div>
							<div className="Tile-Title" >Twenty minutes with a founder.</div>
							<div className="Tile-Text" >
								What you serve, at what volume, and what you would want to trace.
								We show the product on your case, not on slides.
							</div>
							<span className="Contact-Button Contact-Button-Primary" >{ BOOK_DEMO_LABEL }</span>
						</a>

						<div className="Tile Contact-Way" data-fade data-fade-delay="0.28" >
							<div className="Tile-Kicker" >Write to us</div>
							<a className="Contact-Address" href={ `mailto:${ CONTACT_EMAIL }` } >{ CONTACT_EMAIL }</a>
							<div className="Tile-Text" >
								A question, a trial, a partnership: every message lands in a
								founder's inbox.
							</div>
							<CopyButton text={ CONTACT_EMAIL } />
						</div>
					</div>

				</div>
			</div>

			<WhoWeAre />
		</Page>
	)
}
